import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Popover } from '@buildoutinc/blueprint-react/ui/Popover';
import { Progress } from '@buildoutinc/blueprint-react/ui/Progress';
import './styles.scss';

interface Props {
  sent: number;
  limit: number;
  remaining: number;
  percentage: number;
  resetOn: string;
}

interface UsageRingProps {
  percentage: number;
}

const RING_RADIUS = 9;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const clampPercentage = (percentage: number) => Math.min(Math.max(percentage, 0), 100);

function UsageRing({ percentage }: UsageRingProps) {
  const dashOffset = RING_CIRCUMFERENCE * (1 - clampPercentage(percentage) / 100);

  return (
    <svg aria-hidden="true" className="email-usage__ring" height="24" viewBox="0 0 24 24" width="24">
      <circle className="email-usage__ring-track" cx="12" cy="12" r={RING_RADIUS} />
      <circle
        className="email-usage__ring-fill"
        cx="12"
        cy="12"
        r={RING_RADIUS}
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={dashOffset}
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
}

export default function EmailSendLimitWidget({ sent, limit, remaining, percentage, resetOn }: Props) {
  return (
    <Popover>
      <Popover.Trigger
        openOnHover
        render={(
          <div className="email-usage d-inline-flex align-items-center gap-2">
            <UsageRing percentage={percentage} />
            <div className="d-flex flex-column lh-sm">
              <span className="d-inline-flex align-items-center gap-1 fw-semibold">
                Emails Sent
                <FontAwesomeIcon className="email-usage__info" icon={faCircleInfo} />
              </span>
              <span className="fs-small email-usage__muted">
                {sent.toLocaleString()}
                <span className="email-usage__faint"> / {limit.toLocaleString()}</span>
              </span>
            </div>
          </div>
        )}
      />
      <Popover.Content align="end" className="email-usage__popover" side="bottom" sideOffset={8}>
        <Popover.Header className="fw-semibold">Email Send Limit</Popover.Header>
        <Popover.Body>
          <div className="d-flex justify-content-between align-items-baseline mb-2 fs-large">
            <span>
              <span className="fw-semibold">{sent.toLocaleString()}</span>
              <span className="email-usage__faint"> / {limit.toLocaleString()}</span>
            </span>
            <span className="email-usage__faint">{percentage}%</span>
          </div>
          <Progress value={clampPercentage(percentage)} />
          <div className="d-flex gap-3 mt-3">
            <Card className="flex-fill">
              <Card.Body>
                <div className="fs-large fw-semibold">{remaining.toLocaleString()}</div>
                <div className="fs-small email-usage__muted">Emails Left</div>
              </Card.Body>
            </Card>
            <Card className="flex-fill">
              <Card.Body>
                <div className="fs-large fw-semibold">{resetOn}</div>
                <div className="fs-small email-usage__muted">Email Limit Resets</div>
              </Card.Body>
            </Card>
          </div>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
}
